import Handlebars from 'handlebars';

// Bundled card templates (vendored from the retired expert-packages
// _templates catalog). Lazy-loaded as raw HTML on first use so old sessions
// whose cards reference these templates keep rendering with no server round
// trip — the runner's template registry / GCS catalog no longer exists.
const templateSources = import.meta.glob('../templates/*.html', {
  query: '?raw',
  import: 'default',
});

// Per-template metadata ($id, version, category, slots, example) vendored
// alongside the HTML. Used by the playground Cards page.
const templateMetaSources = import.meta.glob('../templates/*.json', {
  import: 'default',
});

/** List the bundled template metadata objects, sorted by $id. */
export async function listBundledTemplates() {
  const out = [];
  for (const loader of Object.values(templateMetaSources)) {
    try {
      const meta = await loader();
      if (meta && meta.$id) out.push(meta);
    } catch {
      /* skip unreadable metadata */
    }
  }
  return out.sort((a, b) => a.$id.localeCompare(b.$id));
}

// In-memory template cache (session lifetime)
const templateCache = new Map();

// ── Template aliases — single source of truth for GET/SSE name divergence ──
// The Postgres snapshot returned by `GET /api/users/sessions/{id}` serializes
// some cards under different template names than the Redis WAL replayed over
// SSE. Same payload shape, different name. Until api-server's
// `session_view.build_session_public_view()` normalizes server-side (FU-2),
// alias on the client at the single ingest point so every downstream consumer
// (store, renderer, raw-data view) sees one canonical name.
export const TEMPLATE_ALIASES = Object.freeze({
  html_block: 'freeform-html', // data.html → same shape as freeform-html
  // `text` is intentionally NOT aliased: it has a dedicated React renderer in
  // CardRenderer that surfaces the message inside a labeled card frame — more
  // useful for the developer-facing playground than reusing the agent-speech
  // bubble. Keeping its name preserves the "TEXT" card header for debug.
});

export function normalizeTemplate(templateId) {
  return TEMPLATE_ALIASES[templateId] || templateId;
}

/**
 * Walk a session-detail response and rewrite card `template` fields through
 * normalizeTemplate. Mutates in place + returns the same object — the GET
 * snapshot path consumes nested `timeline[].cards[id].template` and
 * `result.cards[id].template`; both shapes are handled.
 *
 * Public utility because both `api.getSession` (authenticated GET) and the
 * `SharedSessionView` public-share fetch path need to normalize before
 * downstream code sees the cards.
 */
export function normalizeSessionDetailCards(detail) {
  if (!detail || typeof detail !== 'object') return detail;
  const rewriteCardsMap = (cards) => {
    if (!cards || typeof cards !== 'object') return;
    for (const card of Object.values(cards)) {
      if (card && typeof card === 'object' && typeof card.template === 'string') {
        card.template = normalizeTemplate(card.template);
      }
    }
  };
  if (Array.isArray(detail.timeline)) {
    for (const round of detail.timeline) {
      if (round && typeof round === 'object') rewriteCardsMap(round.cards);
    }
  }
  if (detail.result && typeof detail.result === 'object') {
    rewriteCardsMap(detail.result.cards);
  }
  return detail;
}

/**
 * Register custom Handlebars helpers used by existing templates.
 * Templates like thinking-process.html use {{#if_done}}, {{#if_active}}, etc.
 * These are section helpers that check a status field.
 */
Handlebars.registerHelper('if_done', function(options) {
  return this.status === 'done' ? options.fn(this) : options.inverse(this);
});
Handlebars.registerHelper('if_active', function(options) {
  return this.status === 'active' ? options.fn(this) : options.inverse(this);
});
Handlebars.registerHelper('if_skipped', function(options) {
  return this.status === 'skipped' ? options.fn(this) : options.inverse(this);
});
Handlebars.registerHelper('if_pending', function(options) {
  return this.status === 'pending' ? options.fn(this) : options.inverse(this);
});
// Percentage helper for progress bars
Handlebars.registerHelper('percent', function(value) {
  const num = parseFloat(value);
  return isNaN(num) ? '0' : Math.round(num).toString();
});
// Progress width helper — computes value/max*100, clamped to [0,100]
Handlebars.registerHelper('progressWidth', function(value, max) {
  const v = parseFloat(value) || 0;
  const m = parseFloat(max) || 100;
  return Math.min(100, Math.max(0, (v / m) * 100)).toFixed(1);
});
// Gauge offset helper — computes stroke-dashoffset for SVG circular gauge
Handlebars.registerHelper('gaugeOffset', function(value) {
  const v = Math.min(100, Math.max(0, parseFloat(value) || 0));
  return (377 - (377 * v / 100)).toFixed(1);
});
// Auto gauge color — green/yellow/red based on value
Handlebars.registerHelper('gaugeColor', function(value, color) {
  if (color) return color;
  const v = parseFloat(value) || 0;
  return v > 66 ? '#4caf50' : (v > 33 ? '#ff9800' : '#f44336');
});
// JSON serialization helper — used by mutable card templates to embed slot data
// for replay (e.g. ask-user-question embeds `answers` as JSON for answered state).
Handlebars.registerHelper('json', function(value) {
  try { return JSON.stringify(value ?? {}); } catch { return '{}'; }
});

/**
 * Load and cache a template's HTML source from the bundled set.
 * Returns compiled Handlebars template function, or null if unavailable.
 */
export async function getCompiledTemplate(templateId) {
  templateId = normalizeTemplate(templateId);
  if (templateCache.has(templateId)) {
    return templateCache.get(templateId);
  }

  const loader = templateSources[`../templates/${templateId}.html`];
  if (!loader) {
    templateCache.set(templateId, null);
    return null;
  }

  let html = null;
  try {
    html = await loader();
  } catch {
    /* chunk load failure — treat as unavailable */
  }
  if (!html) {
    templateCache.set(templateId, null);
    return null;
  }

  const compiled = Handlebars.compile(html);
  templateCache.set(templateId, compiled);
  return compiled;
}

/**
 * Render a template with slot data. Returns HTML string or null.
 */
export async function renderTemplate(templateId, data) {
  const compiled = await getCompiledTemplate(templateId);
  if (!compiled) return null;
  try {
    return compiled(data || {});
  } catch (err) {
    console.error(`[templateRenderer] Failed to render ${templateId}:`, err);
    return `<div style="color:#f87171;padding:12px;">Template render error: ${templateId}</div>`;
  }
}

/**
 * Clear the template cache (e.g., on session change).
 */
export function clearTemplateCache() {
  templateCache.clear();
}

/**
 * Extract title from card data for CanvasCard header.
 * Replaces extractModuleTitle from ModuleRenderer.jsx.
 */
export function extractCardTitle(templateId, data) {
  if (!data) return templateId;

  // A NATIVE BLOCK (v0.9 `native-ir`) keeps its fields one level down, under
  // `data.node.data` — the card's own `data` is the envelope. Without this the
  // collapsed header of every block on the surface read "session_block".
  if (data.node?.type) {
    const inner = data.node.data || {};
    return inner.title || inner.text || inner.content || data.node.type.replace(/_/g, ' ');
  }

  if (data.title) return data.title;
  if (data.name) return data.name;
  if (data.location) return data.location;
  if (data.food_name) return data.food_name;

  // Fallback: humanize the template ID
  const names = {
    'hero-image': 'Hero',
    'image-analysis': 'Analysis',
    'comparison-table': 'Comparison',
    'calendar-event': 'Calendar',
    'thinking-process': 'Thinking',
    'info-card': 'Info',
    'session-summary': 'Summary',
    'plant-animal-id': 'Identification',
    'step-guide': 'Guide',
    'text-result': 'Result',
    'file-download': 'Download',
    'document-scan': 'Document Scan',
    'ai-generation': 'Generated',
    'video-player': 'Video',
    'evolution-suggestion': 'Evolution',
    'memory-update': 'Memory Updated',
    'place-card': 'Place',
    'shopping-list': 'Shopping List',
    'nutrition-card': 'Nutrition',
    'checklist': 'Checklist',
    'recipe': 'Recipe',
    'weather': 'Weather',
    'quiz': 'Quiz',
    'map-pins': 'Locations',
    'image-gallery': 'Gallery',
    'conversation': 'Conversation',
    'skeleton': 'Loading',
    'agent-speech': 'VI',
    'md-list': 'List',
    'md-table': 'Table',
    'md-code': 'Code',
    'md-quote': 'Quote',
  };
  return names[templateId] || templateId;
}
